import { Children, cloneElement, isValidElement, useEffect, useId, useRef, useState } from "react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SectionCard, Q, F, SaveRow } from "../components/identityForm";
import { Compass, Award, Fingerprint, CloudOff, RotateCw, ChevronDown } from "lucide-react";
import IdentityFold from "../components/IdentityFold";
import { Field } from "../components/kit/Field";
import { ActionButton } from "../components/kit/ActionButton";
import { SavedCard, FailureCard } from "../components/GoalNotice";
import IdentitySharingCard from "../components/IdentitySharingCard";
import { toast } from "sonner";
import { mergeLoadedIdentity, fieldKey } from "../lib/identityDraft";
import { sectionFillSummary } from "../lib/sectionCardState";

const LEVEL_OPTIONS = [
  { value: "club", label: "Club" },
  { value: "representative", label: "Representative" },
  { value: "first_class", label: "First Class" },
  { value: "professional", label: "Professional" },
];

const FORMAT_OPTIONS = ["T10", "T20", "T20I", "Limited Overs 30", "Limited Overs 40", "List A", "ODI", "First-Class", "Test"];

// Issue #215: the Monthly Check-In (then called the Self-Awareness Check-In
// — #384 renamed the label) left this page too, for the same
// reason and to the same place (Reflections.jsx). "What am I avoiding" is a
// dated, monthly observation about how it is going, not a description of the
// cricketer you are. Unlike the #214 move this one was NOT presentation-only:
// the form here called `/pi/checkins`, a path this API has never had, so it
// had never once saved. The working version lives under Reflections against
// `/api/checkins`, with the same `ci-*` data-testids.
//
// Issue #214: `mental_performance` (the five prompts and the five 1–10
// scores) is no longer part of this page's state. It renders under the
// Reflections tab now — Reflections.jsx — and writes the same section of
// the same `/players/me/identity` payload from there. Leaving it out of
// `empty` and out of the load merge below is what makes that a MOVE rather
// than a duplicate: `save(section)` only ever sends the one section it was
// asked for, and `upsertPlayerIdentity` leaves absent sections untouched,
// so this page saving `vision` can never clobber a reflection.
const empty = {
  vision: {
    cricketer_type: "", level: "", top_ambitions: ["", "", ""], why_play: "",
    long_term_goal: "", short_term_goal: "", playing_role: "", preferred_formats: [],
  },
  strengths_weaknesses: {
    greatest_strengths: "", confidence_skills: "", improvement_areas: "", challenging_situations: "",
    technical_strengths: "", technical_weaknesses: "",
    tactical_strengths: "", tactical_weaknesses: "",
    mental_strengths: "", mental_weaknesses: "",
  },
  game_identity: {
    batter_type: "", bowler_type: "", team_role: "", competitive_advantage: "",
    batting_style: "", bowling_style: "", primary_role: "", secondary_role: "",
    strengths_statement: "",
  },
};

// ---------------------------------------------------------------------------
// #1290 (D298, board PlayerIdentity): the new look draws the same questions in
// kit controls. Each helper below renders EXACTLY the shipped element when
// `r5` is false (the flag-off tab and the first-run walkthrough, D217), so the
// old look's markup does not move.
// ---------------------------------------------------------------------------
const KIT_FONT = "'DM Sans', 'Segoe UI', sans-serif";

// One question and its control: a column, 4px apart (the board's group). In the
// shipped look this is the label and control as siblings, as before.
const LABEL_BIG = { fontSize: 13, fontWeight: 600, color: "var(--nw-ink)" };
const LABEL_SMALL = { fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" };

function Ask({ r5, label, small = false, gap = 4, children }) {
  const labelId = useId();
  if (!r5) return <><Q>{label}</Q>{children}</>;
  // The question names its control (or, for a group of controls, the group).
  const only = Children.count(children) === 1 && isValidElement(children) ? children : null;
  const named = only
    ? cloneElement(only, typeof only.type === "string" ? { role: "group", "aria-labelledby": labelId } : { "aria-labelledby": labelId })
    : children;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap }}>
      <div id={labelId} style={small ? LABEL_SMALL : LABEL_BIG}>{label}</div>
      {named}
    </div>
  );
}

// `big`: the board draws "Preferred formats" as a 13px question, not a small label.
function Fld({ r5, label, big = false, children }) {
  if (!r5) return <F label={label}>{children}</F>;
  return <Ask r5 label={label} small={!big} gap={big ? 6 : 4}>{children}</Ask>;
}

function Area({ r5, rows, ...props }) {
  if (!r5) return <Textarea rows={rows} {...props} />;
  return (
    <textarea
      rows={rows}
      className="nw-kit-input nw-field"
      {...props}
      style={{
        width: "100%",
        minHeight: 68,
        boxSizing: "border-box",
        padding: 12,
        resize: "vertical",
        fontFamily: KIT_FONT,
        fontSize: 15,
        lineHeight: 1.45,
        color: "var(--nw-ink)",
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-control)",
      }}
    />
  );
}

function Line({ r5, ...props }) {
  if (!r5) return <Input {...props} />;
  return <Field {...props} />;
}

function Cluster({ r5, children }) {
  if (!r5) return <div className="grid md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">{children}</div>;
  return <>{children}</>;
}

// The level question is the control's own small label, as the board draws it.
// The native select sits transparent over the whole control so it keeps its
// native picker and a 46px tap box.
function Level({ r5, label, value, onChange }) {
  if (!r5) {
    return (
      <>
        <Q>{label}</Q>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid="pi-level"><SelectValue placeholder="Choose a level" /></SelectTrigger>
          <SelectContent>{LEVEL_OPTIONS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
        </Select>
      </>
    );
  }
  const chosen = LEVEL_OPTIONS.find((l) => l.value === value);
  return (
    <div
      className="nw-field"
      style={{
        position: "relative",
        minWidth: 0,
        minHeight: 46,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 6,
        padding: "0 10px",
        boxSizing: "border-box",
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-control)",
      }}
    >
      <span aria-hidden="true" style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: chosen ? "var(--nw-ink)" : "var(--nw-ink-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {chosen ? chosen.label : "Choose a level"}
        </span>
      </span>
      <ChevronDown size={16} color="var(--nw-navy)" strokeWidth={2.5} aria-hidden="true" style={{ flexShrink: 0 }} />
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        data-testid="pi-level"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", fontSize: 16 }}
      >
        <option value="">Choose a level</option>
        {LEVEL_OPTIONS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
      </select>
    </div>
  );
}

function FormatChip({ r5, format, on, onToggle }) {
  if (!r5) {
    return (
      <button type="button" onClick={onToggle} data-testid={`pi-fmt-${format}`}
        className={`text-xs px-2.5 py-1 rounded-full border transition ${on ? "bg-[#0B2E59] text-white border-[#0B2E59]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>
        {format}
      </button>
    );
  }
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={!!on}
      onClick={onToggle}
      data-testid={`pi-fmt-${format}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: 44,
        boxSizing: "border-box",
        padding: "0 14px",
        borderRadius: 999,
        fontFamily: KIT_FONT,
        fontSize: 14,
        fontWeight: 600,
        cursor: "pointer",
        background: on ? "var(--nw-navy)" : "var(--nw-surface)",
        color: on ? "#FFFFFF" : "var(--nw-ink-lede)",
        border: on ? "1px solid var(--nw-navy)" : "1px solid var(--nw-line-strong)",
      }}
    >
      {format}
    </button>
  );
}

/** The shipped Save row, or the new look's full-width gold action with its three states. */
function SaveBar({ r5, section, label, onSave, busy, testid, status }) {
  if (!r5) return <SaveRow onSave={onSave} busy={busy} testid={testid} />;
  const here = status && status.section === section ? status.kind : null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {here === "failed" && (
        <FailureCard data-testid="pi-save-failed" title="Save failed" body="Your answers are still here. Try again." />
      )}
      <span data-testid={busy ? "pi-save-busy" : undefined} style={{ display: "block" }}>
        <ActionButton
          onClick={onSave}
          disabled={busy}
          aria-busy={busy ? "true" : undefined}
          data-testid={testid}
          style={busy ? { background: "var(--nw-line)", color: "var(--nw-ink-lede)", opacity: 1 } : undefined}
        >
          {busy ? "Saving\u2026" : "Save section"}
        </ActionButton>
      </span>
      {here === "saved" && <SavedCard data-testid="pi-saved">{`${label} saved`}</SavedCard>}
    </div>
  );
}

/**
 * Identity & Vision — the `/development?tab=identity` tab, and step 2 of the
 * first-run walkthrough (issue #289).
 *
 * `embedded` RENDERS THE PLAYER VISION SECTION AND NOTHING ELSE, and that
 * restriction is the single most important product decision on this ticket.
 * This page is three sections and roughly twenty-five questions (four and
 * thirty-five before #214/#215 moved Mental Performance and the check-in to
 * Reflections). Putting all of it between a new player and the app loses
 * people at the door, and the ones it does not lose type filler to get
 * through it — which is worse than an empty section, because filler looks
 * like an answer. So the walk asks for `vision` only; Strengths & Weaknesses
 * and Game Identity stay exactly where they are, on this tab, to be filled
 * in when the player has something to say.
 *
 * `controlRef` / `onVisionChange` are the same contract `PlayerProfile.jsx`
 * carries for step 1 — see its header for why the wrapper drives the save
 * rather than asking the player to press Save and then Continue. All three
 * props are optional and inert on the Development Plan tab.
 */
export default function PlayerIdentity({ embedded = false, controlRef = null, onVisionChange = null, appearance = "shipped" }) {
  // `embedded` is the first-run walkthrough and never takes the new look (D217).
  const r5 = appearance === "r5" && !embedded;
  const [data, setData] = useState(empty);
  const [saving, setSaving] = useState(null);
  // #1290, new look only: which section is open (one at a time), the line that
  // stays under the section that last saved, and whether the GET of the saved
  // answers failed (the sections are then replaced by a card).
  const [openSection, setOpenSection] = useState(null);
  const [status, setStatus] = useState(null);
  const [answersFailed, setAnswersFailed] = useState(false);

  // Issue #344 — the mount race. This form is editable from the first
  // paint and the load below finishes whenever the network says so, which
  // means "the player is typing" and "the server answered" are concurrent
  // events, not sequential ones. Three refs make that safe, and each one
  // is doing a distinct job:
  //
  //   `dataRef`   the draft as it exists RIGHT NOW. `setData` is async, so
  //               the load's `.then` and `save`'s `await` both need a value
  //               React has not necessarily re-rendered yet. Every write
  //               goes through `write()` so the ref and the state can never
  //               disagree.
  //   `dirtyRef`  every field the player has touched, as `section.key`.
  //               The load fills in the rest and leaves these alone —
  //               that is the whole fix (see lib/identityDraft.js).
  //   `loadRef`   the in-flight load. `save` awaits it, because a section
  //               PUT while the load is still out would send `empty` for
  //               every field the player had not reached and blank them on
  //               the server — the same data loss one step later, and the
  //               same "saved" toast on top of it. It always settles: the
  //               fetch is caught, and `api` carries a 15s deadline (#336).
  const dataRef = useRef(empty);
  const dirtyRef = useRef(new Set());
  const loadRef = useRef(null);
  const loadFailedRef = useRef(false);

  const write = (next) => {
    dataRef.current = next;
    setData(next);
    return next;
  };

  /** Record the field as the player's, then apply their edit to it. */
  const edit = (section, key, nextValue) => {
    dirtyRef.current.add(fieldKey(section, key));
    const d = dataRef.current;
    const value = typeof nextValue === "function" ? nextValue(d[section][key]) : nextValue;
    write({ ...d, [section]: { ...d[section], [key]: value } });
  };

  // The GET of the saved answers. The first load and the new look's Try again
  // are the same request, merged the same way (#344): the player's own edits
  // win over whatever the server sends.
  const loadAnswers = () => {
    loadFailedRef.current = false;
    setAnswersFailed(false);
    loadRef.current = api.get("/players/me/identity").then((r) => {
      write(mergeLoadedIdentity({
        blank: empty,
        current: dataRef.current,
        loaded: r.data || {},
        dirty: dirtyRef.current,
      }));
    }).catch(() => {
      loadFailedRef.current = true;
      setAnswersFailed(true);
    });
    return loadRef.current;
  };

  useEffect(() => {
    loadAnswers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (onVisionChange) onVisionChange(data.vision);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.vision]);

  const patch = (section, key, value) => edit(section, key, value);

  const patchAmbition = (idx, value) =>
    edit("vision", "top_ambitions", (cur) => {
      const next = [...(cur || ["", "", ""])];
      next[idx] = value;
      return next;
    });

  const toggleFormat = (fmt) =>
    edit("vision", "preferred_formats", (cur) => {
      const set = new Set(cur || []);
      if (set.has(fmt)) set.delete(fmt); else set.add(fmt);
      return [...set];
    });

  const save = async (section) => {
    setSaving(section);
    setStatus(null);
    try {
      // #344: never send a section the load has not answered for yet.
      await loadRef.current;
      if (loadFailedRef.current) {
        // A PUT replaces the whole section, so saving on top of a draft we
        // never managed to populate deletes whatever is on file. Refusing
        // is the only honest option — and it has to SAY so, because the
        // failure this ticket is about was a blank write reported as a
        // success.
        // The new look has already replaced the sections with the failed card.
        if (!r5) toast.error("Couldn't load your saved answers — reload the page before saving.");
        setSaving(null);
        return false;
      }
      // `dataRef`, not `data`: the load may have merged in the server's
      // fields after the render this call closed over.
      await api.put("/players/me/identity", { [section]: dataRef.current[section] });
      // In the walkthrough the step handing over to the dashboard is the
      // confirmation — same reasoning as PlayerProfile.jsx's save().
      if (r5) setStatus({ section, kind: "saved" });
      else if (!embedded) toast.success(`${sectionLabel(section)} saved`);
      setSaving(null);
      return true;
    } catch (e) {
      if (r5) setStatus({ section, kind: "failed" });
      else toast.error("Save failed");
      setSaving(null);
      return false;
    }
  };

  // Reassigned every render, so it never closes over a stale `data` — see
  // PlayerProfile.jsx's controlRef note.
  if (controlRef) controlRef.current = { save: () => save("vision"), busy: saving === "vision" };

  /**
   * The header state for one folded section (#457).
   *
   * Counted over the questions THIS PAGE owns — `empty`'s keys — and not
   * over `data[section]`'s, because the load merges the server's payload on
   * top of the blank template (`identityDraft.js`), so a field the API grows
   * later would silently inflate the denominator of a badge nobody thought
   * to re-read.
   */
  const summaryFor = (section) =>
    sectionFillSummary(Object.keys(empty[section]).map((k) => data[section][k]));

  // New look: opening a section folds the one that was open.
  const foldTo = (key) => () => {
    // The saved/failed line belongs to the open section; any fold change takes it away.
    setStatus(null);
    setOpenSection((cur) => (cur === key ? null : key));
  };
  const foldVision = foldTo("vision");
  const foldSw = foldTo("strengths_weaknesses");
  const foldGi = foldTo("game_identity");

  const visionBody = (
    <>
        <Ask r5={r5} label="What type of cricketer do you want to become?">
        <Area r5={r5} rows={2} value={data.vision.cricketer_type} onChange={(e) => patch("vision", "cricketer_type", e.target.value)} data-testid="pi-cricketer-type" />
        </Ask>

        <Level r5={r5} label="What level are you aiming for?" value={data.vision.level} onChange={(v) => patch("vision", "level", v)} />

        <Ask r5={r5} small label="What are your top 3 cricket ambitions?">
          <div className={r5 ? undefined : "space-y-2"} style={r5 ? { display: "flex", flexDirection: "column", gap: 12 } : undefined}>
            {[0, 1, 2].map((i) => (
              <Line r5={r5} key={i} placeholder={`Ambition ${i + 1}`} value={data.vision.top_ambitions?.[i] || ""} onChange={(e) => patchAmbition(i, e.target.value)} data-testid={`pi-ambition-${i}`} />
            ))}
          </div>
        </Ask>

        <Ask r5={r5} label="Why do you play cricket?">
        <Area r5={r5} rows={2} value={data.vision.why_play} onChange={(e) => patch("vision", "why_play", e.target.value)} data-testid="pi-why-play" />
        </Ask>

        <Cluster r5={r5}>
          <Fld r5={r5} label="Long-term goal"><Line r5={r5} value={data.vision.long_term_goal} onChange={(e) => patch("vision", "long_term_goal", e.target.value)} data-testid="pi-long-goal" /></Fld>
          <Fld r5={r5} label="Short-term goal"><Line r5={r5} value={data.vision.short_term_goal} onChange={(e) => patch("vision", "short_term_goal", e.target.value)} data-testid="pi-short-goal" /></Fld>
          <Fld r5={r5} label="Playing role"><Line r5={r5} placeholder="e.g. Top-order batter / Fast bowler" value={data.vision.playing_role} onChange={(e) => patch("vision", "playing_role", e.target.value)} data-testid="pi-playing-role" /></Fld>
          <Fld r5={r5} big label="Preferred formats">
            <div className={r5 ? undefined : "flex flex-wrap gap-1.5"} style={r5 ? { display: "flex", flexWrap: "wrap", gap: 6 } : undefined}>
              {FORMAT_OPTIONS.map((f) => (
                <FormatChip key={f} r5={r5} format={f} on={data.vision.preferred_formats?.includes(f)} onToggle={() => toggleFormat(f)} />
              ))}
            </div>
          </Fld>
        </Cluster>

        {!embedded && <SaveBar r5={r5} section="vision" label="Player Vision" status={status} onSave={() => save("vision")} busy={saving === "vision"} testid="pi-save-vision" />}
    </>
  );

  const swBody = (
    <>
        <Ask r5={r5} label="What are your greatest strengths?">
        <Area r5={r5} rows={2} value={data.strengths_weaknesses.greatest_strengths} onChange={(e) => patch("strengths_weaknesses", "greatest_strengths", e.target.value)} data-testid="pi-sw-greatest" />
        </Ask>

        <Ask r5={r5} label="What skills give you confidence?">
        <Area r5={r5} rows={2} value={data.strengths_weaknesses.confidence_skills} onChange={(e) => patch("strengths_weaknesses", "confidence_skills", e.target.value)} data-testid="pi-sw-confidence" />
        </Ask>

        <Ask r5={r5} label="What are your biggest improvement areas?">
        <Area r5={r5} rows={2} value={data.strengths_weaknesses.improvement_areas} onChange={(e) => patch("strengths_weaknesses", "improvement_areas", e.target.value)} data-testid="pi-sw-improve" />
        </Ask>

        <Ask r5={r5} label="What situations challenge you most?">
        <Area r5={r5} rows={2} value={data.strengths_weaknesses.challenging_situations} onChange={(e) => patch("strengths_weaknesses", "challenging_situations", e.target.value)} data-testid="pi-sw-challenge" />
        </Ask>

        <Cluster r5={r5}>
          <Fld r5={r5} label="Technical strengths"><Area r5={r5} rows={2} value={data.strengths_weaknesses.technical_strengths} onChange={(e) => patch("strengths_weaknesses", "technical_strengths", e.target.value)} data-testid="pi-sw-tech-s" /></Fld>
          <Fld r5={r5} label="Technical weaknesses"><Area r5={r5} rows={2} value={data.strengths_weaknesses.technical_weaknesses} onChange={(e) => patch("strengths_weaknesses", "technical_weaknesses", e.target.value)} data-testid="pi-sw-tech-w" /></Fld>
          <Fld r5={r5} label="Tactical strengths"><Area r5={r5} rows={2} value={data.strengths_weaknesses.tactical_strengths} onChange={(e) => patch("strengths_weaknesses", "tactical_strengths", e.target.value)} data-testid="pi-sw-tac-s" /></Fld>
          <Fld r5={r5} label="Tactical weaknesses"><Area r5={r5} rows={2} value={data.strengths_weaknesses.tactical_weaknesses} onChange={(e) => patch("strengths_weaknesses", "tactical_weaknesses", e.target.value)} data-testid="pi-sw-tac-w" /></Fld>
          <Fld r5={r5} label="Mental strengths"><Area r5={r5} rows={2} value={data.strengths_weaknesses.mental_strengths} onChange={(e) => patch("strengths_weaknesses", "mental_strengths", e.target.value)} data-testid="pi-sw-men-s" /></Fld>
          <Fld r5={r5} label="Mental weaknesses"><Area r5={r5} rows={2} value={data.strengths_weaknesses.mental_weaknesses} onChange={(e) => patch("strengths_weaknesses", "mental_weaknesses", e.target.value)} data-testid="pi-sw-men-w" /></Fld>
        </Cluster>

        <SaveBar r5={r5} section="strengths_weaknesses" label="Strengths & Weaknesses" status={status} onSave={() => save("strengths_weaknesses")} busy={saving === "strengths_weaknesses"} testid="pi-save-sw" />
    </>
  );

  const giBody = (
    <>
        <Ask r5={r5} label="What type of batter are you?">
        <Area r5={r5} rows={2} value={data.game_identity.batter_type} onChange={(e) => patch("game_identity", "batter_type", e.target.value)} data-testid="pi-gi-batter" />
        </Ask>

        <Ask r5={r5} label="What type of bowler are you?">
        <Area r5={r5} rows={2} value={data.game_identity.bowler_type} onChange={(e) => patch("game_identity", "bowler_type", e.target.value)} data-testid="pi-gi-bowler" />
        </Ask>

        <Ask r5={r5} label="What role do you play in the team?">
        <Area r5={r5} rows={2} value={data.game_identity.team_role} onChange={(e) => patch("game_identity", "team_role", e.target.value)} data-testid="pi-gi-team" />
        </Ask>

        <Ask r5={r5} label="What is your competitive advantage?">
        <Area r5={r5} rows={2} value={data.game_identity.competitive_advantage} onChange={(e) => patch("game_identity", "competitive_advantage", e.target.value)} data-testid="pi-gi-edge" />
        </Ask>

        <Cluster r5={r5}>
          <Fld r5={r5} label="Batting style"><Line r5={r5} placeholder="Right-hand / Left-hand" value={data.game_identity.batting_style} onChange={(e) => patch("game_identity", "batting_style", e.target.value)} data-testid="pi-gi-bs" /></Fld>
          <Fld r5={r5} label="Bowling style"><Line r5={r5} placeholder="Right-arm medium / Left-arm spin" value={data.game_identity.bowling_style} onChange={(e) => patch("game_identity", "bowling_style", e.target.value)} data-testid="pi-gi-bws" /></Fld>
          <Fld r5={r5} label="Primary role"><Line r5={r5} placeholder="Opening batter" value={data.game_identity.primary_role} onChange={(e) => patch("game_identity", "primary_role", e.target.value)} data-testid="pi-gi-primary" /></Fld>
          <Fld r5={r5} label="Secondary role"><Line r5={r5} placeholder="Part-time spinner" value={data.game_identity.secondary_role} onChange={(e) => patch("game_identity", "secondary_role", e.target.value)} data-testid="pi-gi-secondary" /></Fld>
        </Cluster>

        <div>
          <Fld r5={r5} label="Personal strengths statement">
            <Area r5={r5} rows={3} value={data.game_identity.strengths_statement} onChange={(e) => patch("game_identity", "strengths_statement", e.target.value)}
              placeholder='e.g. "I am an opening batter who scores through patience, shot selection, and strong mental discipline."'
              data-testid="pi-gi-statement" />
          </Fld>
          {data.game_identity.strengths_statement && (
            <div className="mt-2 p-3 rounded-lg border-l-4 border-[#D4A017] bg-[#D4A0170D] text-sm italic text-slate-700">
              "{data.game_identity.strengths_statement}"
            </div>
          )}
        </div>

        <SaveBar r5={r5} section="game_identity" label="Game Identity" status={status} onSave={() => save("game_identity")} busy={saving === "game_identity"} testid="pi-save-gi" />
    </>
  );

  return (
    <div
      className={r5 ? undefined : "space-y-6"}
      style={r5 ? { display: "flex", flexDirection: "column", gap: 12 } : undefined}
      data-testid="player-identity-page"
    >
      {!embedded && !r5 && (
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-slate-500">
            <Compass size={12} /> Identity &amp; Vision
          </div>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-slate-900 mt-1">Who you are as a cricketer</h2>
          {/* Issue #214: "mental state" left this sentence with the Mental
              Performance section — how today went is a reflection, and lives
              under the Reflections tab now. */}
          <p className="text-slate-500 mt-1 max-w-2xl">Capture your ambitions, self-awareness and game identity. This becomes the compass for every goal and drill you set from here on.</p>
        </div>
      )}
      {r5 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontFamily: KIT_FONT, fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>
            Identity &amp; Vision
          </div>
          <h2 style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 20, letterSpacing: "-0.3px", color: "var(--nw-ink)" }}>
            Who you are as a cricketer
          </h2>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink-lede)" }}>
            Capture your ambitions, self-awareness and game identity. This becomes the compass for every goal and drill you set from here on.
          </p>
        </div>
      )}

      {/* Issue #292 — who can read this, stated in words, ABOVE the
          questions. The decision about the audience comes before the
          writing: a player who meets the control after three long sections
          has already decided what to say under an assumption nobody
          corrected. Not rendered in the walkthrough (`embedded`), which
          asks for Player Vision and nothing else and belongs to an account
          that has no linked coach yet — see the component's header. */}
      {!embedded && <IdentitySharingCard appearance={r5 ? "r5" : undefined} />}

      {r5 && answersFailed && (
        <div
          data-testid="pi-load-failed"
          style={{ display: "flex", flexDirection: "column", gap: 8, padding: 16, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CloudOff size={20} color="var(--nw-alert)" strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0 }} />
            <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>Couldn't load your saved answers</div>
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>Reload the page before saving.</div>
          <button
            type="button"
            onClick={loadAnswers}
            data-testid="pi-load-failed-retry"
            style={{
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              minHeight: 44,
              padding: "0 14px",
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line-strong)",
              borderRadius: "var(--nw-radius-control)",
              fontSize: 14,
              fontWeight: 600,
              color: "var(--nw-navy)",
              cursor: "pointer",
            }}
          >
            <RotateCw size={14} strokeWidth={2} aria-hidden="true" />
            Try again
          </button>
        </div>
      )}

      {!(r5 && answersFailed) && (
      <>
      {/* Player Vision */}
      {/* Issue #457 — every section on this tab folds, and arrives FOLDED.
          The reasoning is NOT #378's: nothing here accumulates entries. It is
          that this tab is twenty-five questions in three long sections, and a
          player who comes back to it has come back for ONE of them. Three
          headers, each carrying how far through it they are, is a page that
          says where the work is; the same page unfolded is a scroll that says
          nothing until you have read all of it.

          `collapsible={!embedded}` — the walkthrough (step 2, #289) renders
          this section ALONE and asks the player to fill it in. A fold there
          would hide the only thing on the screen behind a click, and it is
          the one place on this page where the player is guaranteed to be
          writing rather than looking. So the walk gets the plain always-open
          card it has always had, and only the tab folds. */}
      {r5 ? (
      <IdentityFold
        icon={Compass}
        title="Player Vision"
        desc="Where you're heading and why."
        summary={summaryFor("vision")}
        open={openSection === "vision"}
        onToggle={foldVision}
        toggleTestid="pv-toggle"
      >
        {visionBody}
      </IdentityFold>
      ) : (
      <SectionCard
        icon={Compass}
        title="Player Vision"
        desc="Where you're heading and why."
        collapsible={!embedded}
        defaultOpen={false}
        summary={summaryFor("vision")}
        toggleTestid="pv-toggle"
      >
        {visionBody}
      </SectionCard>
      )}

      {/* The other two sections are deliberately absent from the walkthrough
          — see this file's header. */}
      {!embedded && (
      <>
      {/* Strengths & Weaknesses */}
      {r5 ? (
      <IdentityFold
        icon={Award}
        title="Strengths & Weaknesses"
        desc="Self-awareness across technical, tactical and mental dimensions."
        summary={summaryFor("strengths_weaknesses")}
        open={openSection === "strengths_weaknesses"}
        onToggle={foldSw}
        toggleTestid="sw-toggle"
      >
        {swBody}
      </IdentityFold>
      ) : (
      <SectionCard
        icon={Award}
        title="Strengths & Weaknesses"
        desc="Self-awareness across technical, tactical and mental dimensions."
        collapsible
        defaultOpen={false}
        summary={summaryFor("strengths_weaknesses")}
        toggleTestid="sw-toggle"
      >
        {swBody}
      </SectionCard>
      )}

      {/* Game Identity */}
      {r5 ? (
      <IdentityFold
        icon={Fingerprint}
        title="Game Identity"
        desc="How you show up when the match starts."
        summary={summaryFor("game_identity")}
        open={openSection === "game_identity"}
        onToggle={foldGi}
        toggleTestid="gi-toggle"
      >
        {giBody}
      </IdentityFold>
      ) : (
      <SectionCard
        icon={Fingerprint}
        title="Game Identity"
        desc="How you show up when the match starts."
        collapsible
        defaultOpen={false}
        summary={summaryFor("game_identity")}
        toggleTestid="gi-toggle"
      >
        {giBody}
      </SectionCard>
      )}
      </>
      )}
      </>
      )}
    </div>
  );
}

function sectionLabel(k) {
  return {
    vision: "Player Vision",
    strengths_weaknesses: "Strengths & Weaknesses",
    game_identity: "Game Identity",
  }[k] || k;
}
